// Type declaration for Intl.DurationFormat (not yet in TypeScript lib)
declare global {
  namespace Intl {
    interface DurationFormatOptions {
      style?: 'long' | 'short' | 'narrow' | 'digital';
    }
    interface Duration {
      weeks?: number;
      days?: number;
      hours?: number;
      minutes?: number;
      seconds?: number;
    }
    class DurationFormat {
      constructor(locale: string, options?: DurationFormatOptions);
      format(duration: Duration): string;
    }
  }
}

export const formatCurrency = (value: number, includeDecimals: boolean = false): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: includeDecimals ? 2 : 0,
    maximumFractionDigits: includeDecimals ? 2 : 0,
  }).format(value);
};

type DateFormatInput = Date | string | null;

function toDate(date: Date | string): Date {
  return date instanceof Date ? date : new Date(date);
}

export const normalizeToLocalNoon = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
};

export const formatDateInputValue = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export const formatDate = (date: DateFormatInput): string => {
  if (!date) return 'N/A';
  const dateObj = toDate(date);
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const formatDayOfYear = (dateInput: Date | string): string => {
  const date = toDate(dateInput);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

export const formatRelativeDate = (date: string | null): string => {
  if (!date) return 'N/A';
  const dateObj = new Date(date);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - dateObj.getTime()) / 1000);

  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;

  return formatDate(date);
};

export const formatTime = (time: { hour?: number; minute?: number }): string | null => {
  const { hour, minute } = time;
  if (hour === undefined || minute === undefined) return null;

  const date = new Date();
  date.setHours(hour, minute, 0, 0);

  return new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

/** "gateCode" → "Gate Code", for friendlier display-name placeholders and labels. */
export const titleCase = (value: string): string =>
  value
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, char => char.toUpperCase())
    .replace(/\s+/g, ' ')
    .trim();
